import React, { memo } from "react";
import { useIconSpritePosition, Position } from "./adapter-native-icon-utils";
import { Box } from "@mui/material";
import { NewRelease, SegAddOn } from "../customIcons";
import { getIconBackgroundByType } from "../../../../app/components/flow/designer/predefinedComponents/predefinedComponentsDataSource";
import WithTooltip from "../../tooltip/tooltip";

interface AdapterNativeIconProps {
    name: string;
    size: number;
    spriteId?: string;
    type?: string;
    addWrapper?: boolean;
    newlyReleased?: boolean;
    showBorder?: boolean; // Default true
    isAddOn?: boolean;
    tooltip?: React.ReactNode;
    hasBgColor?: boolean;
}

type Size = string | number;

const styles = {
    iconContainer: (size: Size, position?: Position, showBorder?: boolean, hasBgColor?: boolean) => ({
        width: size === 56 ? "54px" : `${size}px`,
        height: size === 56 ? "54px" : `${size}px`,
        border: showBorder ? ({ palette }) => `1px solid ${palette?.greyscale?.[200]}` : "",
        borderRadius: `calc(14 * (${size} / 56) * 1px)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: position ? `url('/src/generic/icon/component-icon/images/adapters-native-icons.png')` : "",
        backgroundSize: `calc(1456 * (${size} / 56) * 1px)`,
        backgroundPosition: position
            ? `calc((${position.x} + 2) * (${size} / 56) * -1px) calc((${position.y} + 2) * (${size} / 56) * -1px)`
            : "",
        position: "relative",
        ...(hasBgColor ? { backgroundColor: "#EBF2FA" } : {})
    }),
    newlyReleaseMarker: (size: Size) => ({
        position: "absolute",
        width: size === 56 ? "19px" : `calc(19 * (${size} / 56) * 1px)`,
        height: size === 56 ? "19px" : `calc(19 * (${size} / 56) * 1px)`,
        borderRadius: "50%",
        top: size === 56 ? "-6px" : `calc(6 * (${size} / 56) * -1px)`,
        right: size === 56 ? "-6px" : `calc(6 * (${size} / 56) * -1px)`,
        backgroundColor: "primary.100",
        border: ({ palette }) => `1px solid ${palette?.primary?.[200]}`,
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    }),
    addOnMarker: (size: Size) => ({
        position: "absolute",
        width: size === 56 ? "19px" : `calc(19 * (${size} / 56) * 1px)`,
        height: size === 56 ? "19px" : `calc(19 * (${size} / 56) * 1px)`,
        borderRadius: "50%",
        bottom: size === 56 ? "-6px" : `calc(6 * (${size} / 56) * -1px)`,
        right: size === 56 ? "-6px" : `calc(6 * (${size} / 56) * -1px)`,
        backgroundColor: "white",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        "& svg": {
            width: "19px",
            height: "19px",
            fill: ({ palette }) => palette.warning[500]
        }
    }),
    iconWrap: (name: string, size: number, type?: string) => ({
        width: size === 56 ? "54px" : `${size}px`,
        height: size === 56 ? "54px" : `${size}px`,
        pt: "1px",
        pl: "1px",
        background: getIconBackgroundByType(name, type),
        borderRadius: "4px"
    })
};

const AdapterNativeIcon: React.FunctionComponent<AdapterNativeIconProps> = ({
    name,
    size = 56,
    spriteId,
    type,
    addWrapper = false,
    newlyReleased = false,
    showBorder = true,
    hasBgColor = false,
    isAddOn = false,
    tooltip = null
}) => {
    const Content = () => (
        <Box
            sx={addWrapper ? styles.iconWrap(name, size, type) : undefined}
            data-testid="adapter-native-icon"
            data-testkey={name + (spriteId ? ":" + spriteId : "")}
        >
            <Box
                data-testid="adapter-native-icon-inner"
                sx={styles.iconContainer(size, useIconSpritePosition(name, spriteId), showBorder, hasBgColor)}
            >
                {isAddOn ? (
                    <Box sx={styles.addOnMarker(size)}>
                        <SegAddOn />
                    </Box>
                ) : newlyReleased ? (
                    <Box sx={styles.newlyReleaseMarker(size)}>
                        <NewRelease />
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
    return tooltip ? WithTooltip(<Content />, tooltip) : <Content />;
};

export default memo(AdapterNativeIcon);
